/**
 * Accept.js – get payment nonce from Authorize.net (card data replaced by token).
 * Script loaded in index.html: https://jstest.authorize.net/v1/Accept.js
 *
 * Credentials come from /onlineOrderSettings/menu API; OnlineOrderSettingsContext
 * stores apiLogInId and publicClientKey in sessionStorage (acceptJsApiLoginID, acceptJsPublicClientKey).
 */

const SESSION_KEY_API_LOGIN_ID = "acceptJsApiLoginID";
const SESSION_KEY_PUBLIC_CLIENT_KEY = "acceptJsPublicClientKey";
const SESSION_KEY_BASIL_API_LOGIN_ID = "acceptJsBasilApiLoginID";
const SESSION_KEY_BASIL_PUBLIC_CLIENT_KEY = "acceptJsBasilPublicClientKey";

const ACCEPT_JS_URL = "https://jstest.authorize.net/v1/Accept.js";

/**
 * Ensure Accept.js is loaded. If not present (e.g. blocked by CSP on dev), try to load it dynamically.
 * Resolves when window.Accept is available; rejects after timeout if it never loads.
 */
export function loadAcceptJs(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Not in browser"));
  }
  if (window.Accept) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const timeout = 15_000;
    const deadline = Date.now() + timeout;
    const check = () => {
      if (window.Accept) {
        resolve();
        return;
      }
      if (Date.now() > deadline) {
        reject(
          new Error(
            "Accept.js did not load. On dev, ensure the server allows https://jstest.authorize.net in Content-Security-Policy script-src."
          )
        );
        return;
      }
      requestAnimationFrame(check);
    };
    const existing = document.querySelector(`script[src="${ACCEPT_JS_URL}"]`);
    if (existing) {
      check();
      return;
    }
    const script = document.createElement("script");
    script.type = "text/javascript";
    script.charset = "utf-8";
    script.src = ACCEPT_JS_URL;
    script.onload = () => {
      const interval = setInterval(() => {
        if (window.Accept) {
          clearInterval(interval);
          resolve();
        } else if (Date.now() > deadline) {
          clearInterval(interval);
          reject(new Error("Accept.js loaded but window.Accept not set. Check CSP allows https://jstest.authorize.net."));
        }
      }, 50);
    };
    script.onerror = () =>
      reject(
        new Error(
          "Accept.js failed to load. Allow https://jstest.authorize.net in Content-Security-Policy script-src (dev server/CDN)."
        )
      );
    document.head.appendChild(script);
  });
}

/** Read Accept.js credentials from sessionStorage (set by menu API response in OnlineOrderSettingsContext) */
function getCredentials(): { apiLoginID: string; publicClientKey: string } {
  if (typeof sessionStorage === "undefined") {
    return { apiLoginID: "", publicClientKey: "" };
  }
  return {
    apiLoginID: sessionStorage.getItem(SESSION_KEY_API_LOGIN_ID)?.trim() ?? "",
    publicClientKey: sessionStorage.getItem(SESSION_KEY_PUBLIC_CLIENT_KEY)?.trim() ?? "",
  };
}

/** Basil credentials: from sessionStorage (menu API – basilPublicClientKey; optional basilApiLogInId). Uses merchant apiLoginID when basil one is not set so delivery tokenization works with only basilPublicClientKey from API. */
export function getBasilCredentials(): { apiLoginID: string; publicClientKey: string } {
  if (typeof sessionStorage === "undefined") {
    return { apiLoginID: "", publicClientKey: "" };
  }
  let apiLoginID =
    sessionStorage.getItem(SESSION_KEY_BASIL_API_LOGIN_ID)?.trim() ??
    (typeof import.meta !== "undefined" && (import.meta as any).env?.VITE_BASIL_ACCEPT_API_LOGIN_ID) ??
    "";
  const publicClientKey = sessionStorage.getItem(SESSION_KEY_BASIL_PUBLIC_CLIENT_KEY)?.trim() ?? "";
  if (!apiLoginID && publicClientKey) {
    apiLoginID = sessionStorage.getItem(SESSION_KEY_API_LOGIN_ID)?.trim() ?? "";
  }
  return { apiLoginID, publicClientKey };
}

/** Result from Accept.js: send dataDescriptor + dataValue to your backend instead of raw card */
export interface AcceptJsOpaqueData {
  dataDescriptor: string;
  dataValue: string;
}

export type AcceptJsCredentials = { apiLoginID: string; publicClientKey: string };

function dispatchAcceptJs(
  cardNumber: string,
  expMonth: string,
  expYear: string,
  cardCode: string,
  credentials: AcceptJsCredentials,
  label: string
): Promise<AcceptJsOpaqueData> {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.Accept) {
      const hint =
        typeof window !== "undefined" && window.location?.protocol === "https:"
          ? " Script may be blocked (e.g. Content Security Policy). Allow https://jstest.authorize.net."
          : " Page must be served over HTTPS.";
      reject(new Error("Accept.js is not loaded." + hint));
      return;
    }

    const { apiLoginID, publicClientKey } = credentials;
    if (!apiLoginID || !publicClientKey) {
      reject(
        new Error(
          label === "basil"
            ? "Basil payment is not configured. Set acceptJsBasilApiLoginID (e.g. from menu API) or VITE_BASIL_ACCEPT_API_LOGIN_ID."
            : "Payment is not configured. Menu API (onlineOrderSettings/menu) must return apiLogInId and publicClientKey."
        )
      );
      return;
    }

    const authData: { clientKey: string; apiLoginID: string } = {
      clientKey: publicClientKey,
      apiLoginID,
    };
    const cardData = {
      cardNumber: cardNumber.replace(/\s/g, ""),
      month: expMonth,
      year: expYear,
      cardCode,
    };
    const secureData = { authData, cardData };

    if (import.meta?.env?.DEV) {
      console.log(`[Accept.js] Tokenization (${label}) started`, { apiLoginID: authData.apiLoginID });
    }

    window.Accept.dispatchData(secureData, (response: {
      messages: { resultCode: string; message: Array<{ text: string }> };
      opaqueData?: { dataDescriptor: string; dataValue: string };
    }) => {
      if (response.messages.resultCode === "Error") {
        const msg = response.messages.message?.map((m) => m.text).join(" ") || "Payment failed.";
        if (import.meta?.env?.DEV) console.log(`[Accept.js] ${label} Error:`, msg);
        reject(new Error(msg));
        return;
      }
      if (!response.opaqueData?.dataValue) {
        reject(new Error("Payment tokenization failed. Please try again."));
        return;
      }
      const dataDescriptor = response.opaqueData.dataDescriptor || "COMMON.ACCEPT.INAPP.PAYMENT";
      const dataValue = response.opaqueData.dataValue;
      if (import.meta?.env?.DEV) {
        console.log(`[Accept.js] ${label} tokenization success`);
      }
      resolve({ dataDescriptor, dataValue });
    });
  });
}

/**
 * Tokenize with merchant credentials (from sessionStorage / menu API).
 * Use for order amount.
 */
export function tokenizeCardWithAcceptJs(
  cardNumber: string,
  expMonth: string,
  expYear: string,
  cardCode: string
): Promise<AcceptJsOpaqueData> {
  return dispatchAcceptJs(
    cardNumber,
    expMonth,
    expYear,
    cardCode,
    getCredentials(),
    "merchant"
  );
}

/**
 * Tokenize with explicit credentials (e.g. Basil for delivery fees/tips).
 * Use for delivery: call once with merchant key (order), once with basil key (fees/tips).
 */
export function tokenizeCardWithAcceptJsWithCredentials(
  cardNumber: string,
  expMonth: string,
  expYear: string,
  cardCode: string,
  credentials: AcceptJsCredentials
): Promise<AcceptJsOpaqueData> {
  return dispatchAcceptJs(
    cardNumber,
    expMonth,
    expYear,
    cardCode,
    credentials,
    "basil"
  );
}

/** Parse "MM/YY" or "MM/YYYY" into { month, year } for Accept.js cardData */
export function parseExpiration(expirationDate: string): { month: string; year: string } {
  const parts = String(expirationDate || "").split("/").map((p) => p.trim());
  let month = parts[0] || "";
  let year = parts[1] || "";
  if (year.length === 4) year = year.slice(-2);
  return { month, year };
}
